import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import TabView from "../../../../../components/misc/TabView";
import { useToast } from "../../../../../contexts/ToastContext";
import apiService from "../../../../../services/api";
import { GamesList } from "../../../../games/GameHistory/parts";
import { Members } from "./parts";

const tabs = [
  { label: "Upcoming games", value: "games" },
  { label: "Members", value: "members" },
  { label: "History", value: "history" },
];

// The backend returns pagination metadata as current_page/items_per_page,
// but "load more" here works off page/limit - normalize so pagination state
// always has both, regardless of which key the backend used for either.
const normalizePagination = (pagination) => ({
  ...pagination,
  page: pagination?.page ?? pagination?.current_page,
  limit: pagination?.limit ?? pagination?.items_per_page,
});
// Define custom colors for each tab's content area
const tabContentColors = {
  games: "#F5F5F5", // Light blue for upcoming games
  members: "#F5F5F5", // Light green for members
};

export const Tabs = () => {
  const { groupId } = useParams();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState("games");

  const upcomingObserverRef = useRef(null);
  const historyObserverRef = useRef(null);
  const membersObserverRef = useRef(null);

  // Upcoming games state
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [upcomingGamesPagination, setUpcomingGamesPagination] = useState({
    page: 1,
    limit: 20,
    has_next_page: false,
  });
  const [upcomingGamesLoading, setUpcomingGamesLoading] = useState(true);
  const [upcomingGamesLoadingMore, setUpcomingGamesLoadingMore] =
    useState(false);

  // History games state
  const [historyGames, setHistoryGames] = useState([]);
  const [historyGamesPagination, setHistoryGamesPagination] = useState({
    page: 1,
    limit: 20,
    has_next_page: false,
  });
  const [historyGamesLoading, setHistoryGamesLoading] = useState(true);
  const [historyGamesLoadingMore, setHistoryGamesLoadingMore] = useState(false);

  // Members state
  const [members, setMembers] = useState([]);
  const [membersPagination, setMembersPagination] = useState({
    page: 1,
    limit: 20,
    has_next_page: false,
  });
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersLoadingMore, setMembersLoadingMore] = useState(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Fetch upcoming games
  const fetchUpcomingGames = async (params, append = false) => {
    try {
      if (append) {
        setUpcomingGamesLoadingMore(true);
      } else {
        setUpcomingGamesLoading(true);
      }

      const response = await apiService.fetchGames({
        group_id: groupId,
        from: new Date().getTime(),
        ...params,
      });

      if (response.success) {
        if (append) {
          setUpcomingGames((prev) => [...prev, ...response.data]);
        } else {
          setUpcomingGames(response.data);
        }
        setUpcomingGamesPagination(normalizePagination(response.pagination));
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      if (append) {
        setUpcomingGamesLoadingMore(false);
      } else {
        setUpcomingGamesLoading(false);
      }
    }
  };

  // Fetch history games
  const fetchHistoryGames = async (params, append = false) => {
    try {
      if (append) {
        setHistoryGamesLoadingMore(true);
      } else {
        setHistoryGamesLoading(true);
      }

      const response = await apiService.fetchGames({
        group_id: groupId,
        to: new Date().getTime(),
        ...params,
      });

      if (response.success) {
        if (append) {
          setHistoryGames((prev) => [...prev, ...response.data]);
        } else {
          setHistoryGames(response.data);
        }
        setHistoryGamesPagination(normalizePagination(response.pagination));
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      if (append) {
        setHistoryGamesLoadingMore(false);
      } else {
        setHistoryGamesLoading(false);
      }
    }
  };

  // Fetch members
  const fetchMembers = async (params, append = false) => {
    try {
      if (append) {
        setMembersLoadingMore(true);
      } else {
        setMembersLoading(true);
      }

      const response = await apiService.fetchGroupMembers(groupId, params);

      if (response.success) {
        if (append) {
          setMembers((prev) => [...prev, ...response.data]);
        } else {
          setMembers(response.data);
        }
        setMembersPagination(normalizePagination(response.pagination));
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      if (append) {
        setMembersLoadingMore(false);
      } else {
        setMembersLoading(false);
      }
    }
  };

  // Load more upcoming games
  const loadMoreUpcomingGames = () => {
    if (
      !upcomingGamesLoadingMore &&
      upcomingGamesPagination.has_next_page &&
      activeTab === "games"
    ) {
      const nextPage = upcomingGamesPagination.page + 1;
      fetchUpcomingGames(
        { page: nextPage, limit: upcomingGamesPagination.limit },
        true,
      );
    }
  };

  // Load more history games
  const loadMoreHistoryGames = () => {
    if (
      !historyGamesLoadingMore &&
      historyGamesPagination.has_next_page &&
      activeTab === "history"
    ) {
      const nextPage = historyGamesPagination.page + 1;
      fetchHistoryGames(
        { page: nextPage, limit: historyGamesPagination.limit },
        true,
      );
    }
  };

  // Load more members
  const loadMoreMembers = () => {
    if (
      !membersLoadingMore &&
      membersPagination.has_next_page &&
      activeTab === "members"
    ) {
      const nextPage = membersPagination.page + 1;
      fetchMembers({ page: nextPage, limit: membersPagination.limit }, true);
    }
  };

  // Initial load when tab changes
  useEffect(() => {
    if (activeTab === "games" && upcomingGames.length === 0) {
      fetchUpcomingGames({
        page: 1,
        limit: 20,
      });
    } else if (activeTab === "history" && historyGames.length === 0) {
      fetchHistoryGames({
        page: 1,
        limit: 20,
      });
    } else if (activeTab === "members" && members.length === 0) {
      fetchMembers({
        page: 1,
        limit: 20,
      });
    }
  }, [activeTab]);

  // Intersection Observer for upcoming games
  useEffect(() => {
    if (activeTab !== "games") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          upcomingGamesPagination.has_next_page &&
          !upcomingGamesLoadingMore
        ) {
          loadMoreUpcomingGames();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    if (upcomingObserverRef.current) {
      observer.observe(upcomingObserverRef.current);
    }

    return () => {
      if (upcomingObserverRef.current) {
        observer.unobserve(upcomingObserverRef.current);
      }
    };
  }, [
    upcomingGamesPagination.has_next_page,
    upcomingGamesLoadingMore,
    activeTab,
    loadMoreUpcomingGames,
  ]);

  // Intersection Observer for history games
  useEffect(() => {
    if (activeTab !== "history") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          historyGamesPagination.has_next_page &&
          !historyGamesLoadingMore
        ) {
          loadMoreHistoryGames();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    if (historyObserverRef.current) {
      observer.observe(historyObserverRef.current);
    }

    return () => {
      if (historyObserverRef.current) {
        observer.unobserve(historyObserverRef.current);
      }
    };
  }, [
    historyGamesPagination.has_next_page,
    historyGamesLoadingMore,
    activeTab,
    loadMoreHistoryGames,
  ]);

  // Intersection Observer for members
  useEffect(() => {
    if (activeTab !== "members") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          membersPagination.has_next_page &&
          !membersLoadingMore
        ) {
          loadMoreMembers();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      },
    );

    if (membersObserverRef.current) {
      observer.observe(membersObserverRef.current);
    }

    return () => {
      if (membersObserverRef.current) {
        observer.unobserve(membersObserverRef.current);
      }
    };
  }, [
    membersPagination.has_next_page,
    membersLoadingMore,
    activeTab,
    loadMoreMembers,
  ]);

  return (
    <Box
      sx={{
        pb: { xs: 16, md: 0 },
        mx: { xs: -2, md: 0 },
        borderBottomLeftRadius: { md: 8 },
        borderBottomRightRadius: { md: 8 },
        overflow: "hidden",
      }}
    >
      {/* Tab Navigation */}
      <TabView
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabContentColors={tabContentColors}
      >
        {/* Tab Content with padding to show background color */}
        <Box sx={{ p: 2, height: "100%" }}>
          {activeTab === "games" && (
            <GamesList
              games={upcomingGames}
              pagination={upcomingGamesPagination}
              observerRef={upcomingObserverRef}
              loadingMore={upcomingGamesLoadingMore}
              loading={upcomingGamesLoading}
              emptyMessage="No upcoming games."
            />
          )}
          {activeTab === "members" && (
            <Members
              members={members}
              pagination={membersPagination}
              loading={membersLoading}
              loadingMore={membersLoadingMore}
              observerRef={membersObserverRef}
            />
          )}
          {activeTab === "history" && (
            <GamesList
              games={historyGames}
              pagination={historyGamesPagination}
              observerRef={historyObserverRef}
              loadingMore={historyGamesLoadingMore}
              loading={historyGamesLoading}
              emptyMessage="No game history."
            />
          )}
        </Box>
      </TabView>
    </Box>
  );
};
